/**
 * Performance reviews (PRD Module 3).
 *
 * Scores are strings, the same as money: the API sends NUMERIC(5,2) as text, and a review
 * score is a number somebody argued for in a meeting. Nothing here converts one to a
 * `number` except for display and for the width of a bar.
 *
 * Two things about this API shape the screens more than anything else.
 *
 * `overall_score` and `rating` are *derived* — the server recomputes them from the four
 * components on every write and refuses to accept them. So there is no field for them on
 * any form here, and a review missing a component comes back with both as `null` rather
 * than with a score computed from what happened to be filled in.
 *
 * `metrics` is written only by `refreshMetrics`. It is the provenance of the two computed
 * component scores — the revenue found, the target it was measured against, the orders
 * counted — and it is what makes the number defensible six months later when the rep
 * disputes it and the orders have moved on to other statuses.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const REVIEW_STATUSES = ['draft', 'submitted', 'approved', 'closed'] as const

export type ReviewStatus = (typeof REVIEW_STATUSES)[number]

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting approval',
  approved: 'Approved',
  closed: 'Closed',
}

export const REVIEW_RATINGS = [
  'exceeds',
  'meets',
  'below',
  'needs_improvement',
  'unsatisfactory',
] as const

export type ReviewRating = (typeof REVIEW_RATINGS)[number]

/** PRD section 22 "Rating Scale", in the words the PRD uses. */
export const REVIEW_RATING_LABELS: Record<ReviewRating, string> = {
  exceeds: 'Exceeds expectations',
  meets: 'Meets expectations',
  below: 'Below expectations',
  needs_improvement: 'Needs improvement',
  unsatisfactory: 'Unsatisfactory',
}

/** The four components and what each one is worth. Mirrors `SCORE_WEIGHTS` in the API. */
export const SCORE_COMPONENTS = [
  { key: 'sales_score', label: 'Sales achievement', weight: 0.3, computed: true },
  { key: 'kpi_score', label: 'KPI completion', weight: 0.3, computed: true },
  { key: 'attendance_score', label: 'Attendance', weight: 0.2, computed: true },
  { key: 'manager_score', label: "Manager's assessment", weight: 0.2, computed: false },
] as const

export type ScoreKey = (typeof SCORE_COMPONENTS)[number]['key']

export interface Goal {
  title: string
  detail?: string | null
  due_date?: string | null
}

export interface Review {
  id: string
  employee_id: string
  employee_code: string | null
  employee_name: string | null
  employee_position: string | null
  reviewer_id: string
  review_period_start: string
  review_period_end: string
  review_date: string
  self_assessment: string | null
  manager_assessment: string | null
  goals: Goal[] | null
  development_plan: Goal[] | null
  sales_score: string | null
  attendance_score: string | null
  kpi_score: string | null
  manager_score: string | null
  overall_score: string | null
  rating: ReviewRating | null
  metrics: ReviewMetrics | null
  status: ReviewStatus
  signoff_by_employee: boolean
  signoff_by_manager: boolean
  signoff_at: string | null
  created_at: string
  updated_at: string
  /** Rules-based, computed by the server from the scores and metrics. Needs no AI. */
  recommendations?: ReviewRecommendation[]
}

export interface ReviewRecommendation {
  area: 'sales' | 'attendance' | 'kpi' | 'manager' | 'strength'
  priority: 'high' | 'medium' | 'low'
  text: string
}

/**
 * What the computed scores were computed from.
 *
 * Every branch carries an `unavailable` line rather than leaving the reader to work out
 * why a figure is missing. "No sales target on record" and "no orders in the review
 * period" are different problems with different fixes, and the screen says which.
 */
export interface ReviewMetrics {
  computed_at: string
  sales: {
    revenue: string
    monthly_target: string | null
    period_target: string | null
    achievement: string | null
    unavailable: string | null
  }
  kpi: {
    orders_committed: number
    orders_fulfilled: number
    fulfilment_rate: string | null
    unavailable: string | null
  }
  attendance: {
    expected_days: number
    present_days: number
    rate: string | null
    unavailable: string | null
  }
  source: {
    table: string
    order_date_from: string
    order_date_to: string
    counted_statuses: string[]
    earned_statuses: string[]
    attendance_table: string
    route_date_from: string
    route_date_to: string
  }
}

/** A person a review can be opened on. Not an employee directory; see the API schema. */
export interface ReviewEmployee {
  id: string
  employee_code: string
  full_name: string
  position: string
  department: string | null
  status: string
  sales_target_monthly: string | null
}

export interface ReviewInput {
  employee_id: string
  review_period_start: string
  review_period_end: string
  self_assessment?: string | null
  manager_assessment?: string | null
  sales_score?: string | null
  attendance_score?: string | null
  kpi_score?: string | null
  manager_score?: string | null
}

export type ReviewPatch = Partial<Omit<ReviewInput, 'employee_id'>>

const BASE = `${API_PREFIX}/performance-reviews`

export interface ReviewFilters {
  page?: number
  employee_id?: string
  status?: ReviewStatus | ''
  rating?: ReviewRating | ''
  period_start_from?: string
}

function query(filters: ReviewFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.employee_id) params.set('employee_id', filters.employee_id)
  if (filters.status) params.set('status', filters.status)
  if (filters.rating) params.set('rating', filters.rating)
  if (filters.period_start_from) params.set('period_start_from', filters.period_start_from)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listReviews = (filters: ReviewFilters = {}) =>
  apiFetch<Page<Review>>(`${BASE}${query(filters)}`)

/** The backend aliases `fmt` to `format` on this one endpoint -- an inconsistency with
 *  every other export route, not a typo, so it stays exactly as the API declares it. */
export const exportReviews = (filters: ReviewFilters, format: ExportFormat) => {
  const params = new URLSearchParams({ format })
  if (filters.employee_id) params.set('employee_id', filters.employee_id)
  if (filters.status) params.set('status', filters.status)
  if (filters.rating) params.set('rating', filters.rating)
  return apiBlob(`${BASE}/export?${params}`)
}

export const getReview = (id: string) => apiGet<Review>(`${BASE}/${id}`)

/**
 * Everyone the caller may file a review about.
 *
 * `page_size=200` and no paging: this fills a `<select>`, and a picker missing the person
 * you are looking for is worse than a long list. A distributor with more than two hundred
 * reviewable staff wants a search box, not a second page of options.
 */
export const listReviewEmployees = () =>
  apiFetch<Page<ReviewEmployee>>(`${BASE}/employees?page_size=200`)

export const createReview = (input: ReviewInput) =>
  apiGet<Review>(BASE, { method: 'POST', body: JSON.stringify(input) })

export const updateReview = (id: string, patch: ReviewPatch) =>
  apiGet<Review>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })

/** Recomputes the sales and KPI halves from the orders. Leaves the typed scores alone. */
export const refreshMetrics = (id: string) =>
  apiGet<Review>(`${BASE}/${id}/metrics`, { method: 'POST' })

export const submitReview = (id: string) =>
  apiGet<Review>(`${BASE}/${id}/submit`, { method: 'POST' })

export const approveReview = (id: string) =>
  apiGet<Review>(`${BASE}/${id}/approve`, { method: 'POST' })

export const signOffReview = (id: string, sides: { as_employee?: boolean; as_manager?: boolean }) =>
  apiGet<Review>(`${BASE}/${id}/signoff`, { method: 'POST', body: JSON.stringify(sides) })

export const deleteReview = (id: string) => apiFetch<void>(`${BASE}/${id}`, { method: 'DELETE' })

/** One decimal place, or an em dash. A missing score is not a zero. */
export function formatScore(score: string | null): string {
  return score === null ? '—' : Number(score).toFixed(1)
}

/** A ratio as a percentage, for achievement and fulfilment rates. */
export function formatRatio(ratio: string | null): string {
  return ratio === null ? '—' : `${(Number(ratio) * 100).toFixed(0)}%`
}

export function formatPeriod(start: string, end: string): string {
  const format = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  return `${format(start)} – ${format(end)}`
}

/**
 * Why this review has no overall score, in the PRD's own words, or null when it has one.
 *
 * The server refuses to move an unscored review through the workflow with "Insufficient
 * data for evaluation period". Saying so on the screen before the button is pressed is
 * the difference between a form that guides and a form that rejects.
 */
export function missingScores(review: Review): ScoreKey[] {
  return SCORE_COMPONENTS.filter((component) => review[component.key] === null).map(
    (component) => component.key,
  )
}
